import { ShowBase, useShowContext } from "ra-core";
import { Link } from "react-router";

import { Badge } from "@/components/ui/badge";

import { usePermission } from "../providers/usePermission";
import {
  AddMembershipForm,
  CompanyEditForm,
  EndMembershipButton,
} from "./CompanyEditor";
import { GraphError, GraphLoading } from "./GraphStatus";
import { GraphTimeline } from "./GraphTimeline";
import { type GraphLink, GraphLinkList } from "./links";
import { RecordCard, RecordRow } from "./record/RecordCard";
import { type ExternalId, ExternalIdsCard } from "./record/ExternalIdsCard";
import { type Assignment, TeamCard } from "./record/TeamCard";
import { RecordHeader } from "./record/RecordHeader";
import { humanize, MUTED, RECORD_LINK } from "./record/styles";

interface GraphCompany {
  id: string;
  name: string;
  kind_id: string | null;
  parent: { id: string; name: string } | null;
  children: { id: string; name: string; kind_id: string | null }[];
  people: {
    id: string;
    contact_id: string;
    first_name: string;
    last_name: string;
    role: string | null;
  }[];
  referred_deals: {
    id: string;
    name: string;
    stage_label: string | null;
    agent_id: string;
    agent_name: string;
  }[];
  links?: GraphLink[];
  assignments?: Assignment[];
  external_ids?: ExternalId[];
}

export function GraphCompanyShow() {
  return (
    <ShowBase queryOptions={{ onError: () => undefined }}>
      <CompanyGraph />
    </ShowBase>
  );
}

/**
 * A company (W12 U3): header with its kind and parent, then its children on
 * the left, the activity in the middle, and its
 * people, referred deals and links on the right.
 */
function CompanyGraph() {
  const { record, isPending, error } = useShowContext<GraphCompany>();
  const canEdit = usePermission("edit_details");
  if (isPending) return <GraphLoading shape="record" />;
  if (error || !record) return <GraphError error={error} noun="company" />;

  const children = record.children ?? [];
  const people = record.people ?? [];
  const referred = record.referred_deals ?? [];
  const links = record.links ?? [];

  return (
    <div className="space-y-6">
      <RecordHeader
        name={record.name}
        shape="square"
        chips={
          record.kind_id ? (
            <Badge variant="secondary" className="font-normal">
              {record.kind_id}
            </Badge>
          ) : null
        }
        facts={
          record.parent ? (
            <span>
              under{" "}
              <Link
                className={RECORD_LINK + " font-normal text-muted-foreground"}
                to={`/companies/${record.parent.id}/show`}
              >
                {record.parent.name}
              </Link>
            </span>
          ) : null
        }
        actions={canEdit ? <CompanyEditForm company={record} /> : null}
      />

      <div className="grid grid-cols-[minmax(0,1fr)] items-start gap-6 lg:grid-cols-[minmax(0,20rem)_minmax(0,1fr)] xl:grid-cols-[minmax(0,20rem)_minmax(0,1fr)_minmax(0,22rem)] 2xl:grid-cols-[minmax(0,22rem)_minmax(0,1fr)_minmax(0,24rem)]">
        <div className="space-y-6">
          {/* The parent is in the header ("under …"). */}
          <RecordCard
            title="Children"
            count={children.length}
            isEmpty={children.length === 0}
          >
            <ul>
              {children.map((child) => (
                <RecordRow
                  key={child.id}
                  primary={
                    <Link
                      className={RECORD_LINK}
                      to={`/companies/${child.id}/show`}
                    >
                      {child.name}
                    </Link>
                  }
                  secondary={child.kind_id ? humanize(child.kind_id) : null}
                />
              ))}
            </ul>
          </RecordCard>
        </div>

        <div className="min-w-0 space-y-6">
          <RecordCard title="Activity" className="pb-5">
            <GraphTimeline resource="companies" id={record.id} />
          </RecordCard>
        </div>

        {/* Two across under the others when there is no room for a third column. */}
        <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:col-span-2 lg:grid-cols-2 xl:col-span-1 xl:grid-cols-1">
          <RecordCard title="People" count={people.length}>
            {people.length ? (
              <ul>
                {people.map((person) => (
                  <RecordRow
                    key={person.contact_id}
                    primary={
                      <Link
                        className={RECORD_LINK}
                        to={`/contacts/${person.contact_id}/show`}
                      >
                        {person.first_name} {person.last_name}
                      </Link>
                    }
                    secondary={person.role}
                    trailing={
                      canEdit ? (
                        <EndMembershipButton
                          affiliationId={person.id}
                          label={`${person.first_name} ${person.last_name}'s membership`}
                        />
                      ) : null
                    }
                  />
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted-foreground">None</p>
            )}
            {canEdit ? <AddMembershipForm companyId={record.id} /> : null}
          </RecordCard>

          <TeamCard assignments={record.assignments} />

          <ExternalIdsCard ids={record.external_ids} />

          <RecordCard
            title="Referred deals"
            count={referred.length}
            isEmpty={referred.length === 0}
          >
            <ul>
              {referred.map((deal) => (
                <RecordRow
                  key={`${deal.id}-${deal.agent_id}`}
                  primary={
                    <>
                      <Link
                        className={RECORD_LINK}
                        to={`/deals/${deal.id}/show`}
                      >
                        {deal.name}
                      </Link>
                      {deal.stage_label ? (
                        <span className={MUTED}> · {deal.stage_label}</span>
                      ) : null}
                    </>
                  }
                  secondary={
                    <>
                      referred by{" "}
                      <Link
                        className={RECORD_LINK + " font-normal"}
                        to={`/contacts/${deal.agent_id}/show`}
                      >
                        {deal.agent_name}
                      </Link>
                    </>
                  }
                />
              ))}
            </ul>
          </RecordCard>

          {/* No company link type is seeded yet; the card appears once one is. */}
          {links.length ? (
            <RecordCard title="Links" count={links.length}>
              <GraphLinkList links={links} showType />
            </RecordCard>
          ) : null}
        </div>
      </div>
    </div>
  );
}
